import { useRef, useCallback } from 'react';
import { Shape } from '../types/Shape';

const MAX_HISTORY_SIZE = 100;

interface UseHistoryReturn {
  pushHistory: (shapes: Shape[]) => void;
  undo: () => Shape[] | null;
  redo: () => Shape[] | null;
  canUndo: () => boolean;
  canRedo: () => boolean;
  clearHistory: () => void;
}

export const useHistory = (): UseHistoryReturn => {
  const historyRef = useRef<Shape[][]>([]);
  const historyIndexRef = useRef(-1);
  const isUndoRedoAction = useRef(false);

  const pushHistory = useCallback((shapes: Shape[]) => {
    // undo/redo 액션 중에는 히스토리 추가하지 않음
    if (isUndoRedoAction.current) {
      isUndoRedoAction.current = false;
      return;
    }

    // 현재 인덱스 이후의 히스토리는 제거 (새로운 액션으로 분기)
    historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1);
    
    // 새로운 상태 추가 (deep copy)
    historyRef.current.push(JSON.parse(JSON.stringify(shapes)));
    
    // 최대 크기 유지
    if (historyRef.current.length > MAX_HISTORY_SIZE) {
      historyRef.current.shift();
    } else {
      historyIndexRef.current++;
    }
    
  }, []);

  const undo = useCallback((): Shape[] | null => {
    if (historyIndexRef.current > 0) {
      historyIndexRef.current--;
      isUndoRedoAction.current = true;
      const prevShapes = historyRef.current[historyIndexRef.current];
      return JSON.parse(JSON.stringify(prevShapes));
    }
    return null;
  }, []);

  const redo = useCallback((): Shape[] | null => {
    if (historyIndexRef.current < historyRef.current.length - 1) {
      historyIndexRef.current++;
      isUndoRedoAction.current = true;
      const nextShapes = historyRef.current[historyIndexRef.current];
      return JSON.parse(JSON.stringify(nextShapes));
    }
    return null;
  }, []);

  const canUndo = useCallback(() => {
    return historyIndexRef.current > 0;
  }, []);

  const canRedo = useCallback(() => {
    return historyIndexRef.current < historyRef.current.length - 1;
  }, []);

  const clearHistory = useCallback(() => {
    historyRef.current = [];
    historyIndexRef.current = -1;
  }, []);

  return {
    pushHistory,
    undo,
    redo,
    canUndo,
    canRedo,
    clearHistory,
  };
};

